import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { LabelWithHint } from '@/components/help/LabelWithHint';
import { socialApi, type ContentPillar, type SocialPost } from '@/lib/social-api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

interface QuickIdeaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pillars: ContentPillar[];
  /** Vorbelegtes Datum, wenn aus einer Kalenderzelle heraus geöffnet. */
  defaultDate?: string | null;
  onCreated: (post: SocialPost) => void;
}

/**
 * Schnelles Erfassen einer Idee — bewusst ohne Kanalwahl.
 * Kanäle, Text und Medien kommen später im Composer dazu.
 */
export function QuickIdeaDialog({
  open,
  onOpenChange,
  pillars,
  defaultDate,
  onCreated,
}: QuickIdeaDialogProps) {
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [pillarId, setPillarId] = useState<string | null>(null);
  const [plannedAt, setPlannedAt] = useState(defaultDate ?? '');
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setTitle('');
    setNotes('');
    setPillarId(null);
    setPlannedAt(defaultDate ?? '');
  };

  const submit = async () => {
    if (!title.trim()) {
      toast.error('Bitte einen Titel für die Idee eingeben.');
      return;
    }
    setSaving(true);
    try {
      const created = await socialApi.createPost({
        title: title.trim(),
        post_kind: 'feed',
        notes: notes.trim() || null,
        pillar_id: pillarId,
        planned_at: plannedAt ? new Date(plannedAt).toISOString() : null,
        board_status: 'idea',
        targets: [],
      });
      toast.success('Idee gespeichert.');
      onCreated(created);
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Neue Idee</DialogTitle>
          <DialogDescription>
            Halte den Gedanken fest. Kanäle, Text und Medien kommen später im Composer dazu.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <LabelWithHint label="Titel" hint="Kurze Arbeitsbezeichnung, z. B. „Case Study Swisscom“." />
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) void submit();
              }}
              className="mt-2 w-full rounded-lg border border-border bg-card px-3 py-2 text-sm"
              placeholder="Worum geht es?"
              maxLength={300}
            />
          </div>

          <div>
            <LabelWithHint label="Notiz" hint="Stichworte, Quellen, Kernbotschaft — alles, was beim Schreiben hilft." />
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="mt-2 w-full rounded-lg border border-border bg-card px-3 py-2 text-sm"
              placeholder="Optional"
            />
          </div>

          {pillars.length > 0 && (
            <div>
              <LabelWithHint label="Säule" hint="Ordnet die Idee einem Themenschwerpunkt zu — hilft, die Mischung im Blick zu behalten." />
              <div className="mt-2 flex flex-wrap gap-1.5">
                {pillars.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPillarId(pillarId === p.id ? null : p.id)}
                    className={cn(
                      'flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-medium transition',
                      pillarId === p.id
                        ? 'border-border bg-primary text-primary-foreground'
                        : 'border-border text-muted'
                    )}
                  >
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: p.color }} />
                    {p.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <LabelWithHint label="Geplant für" hint="Leer lassen, dann landet die Idee im Backlog und kann später in den Kalender gezogen werden." />
            <input
              type="datetime-local"
              value={plannedAt}
              onChange={(e) => setPlannedAt(e.target.value)}
              className="mt-2 rounded-lg border border-border bg-card px-3 py-2 text-sm"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Abbrechen
          </Button>
          <Button onClick={() => void submit()} disabled={saving}>
            {saving && <Loader2 size={14} className="mr-1.5 animate-spin" />}
            Idee speichern
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
